import React, { useCallback, useState } from 'react';
import { CalendarTransferModal } from '../../components/calendar/CalendarTransferModal';
import { useAthleteContext } from '../../context/AthleteContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CalendarGridItem } from '../../hooks/useCalendarData';
import type { CalendarWorkout } from '../../services/api';
import type { CalendarFileDropResult } from '../../utils/calendarFileDrop';
import { calendarActions, forms, healthDocuments } from '../../services/api';
import { useConfirm } from '../../components/NmConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { CalendarWorkoutCard } from './CalendarWorkoutCard';
import { CalendarMetricRow } from './CalendarMetricRow';
import { CalendarEventRow } from './CalendarEventRow';
import { CalendarNoteRow } from './CalendarNoteRow';
import { CalendarHealthDocumentRow } from '../../components/calendar/CalendarHealthDocumentRow';
import {
  CalendarItemActionSheet,
  type CalendarActionTarget,
} from './CalendarItemActionSheet';
import { CalendarUploadOverlay } from '../../components/CalendarUploadOverlay';
import { useCalendarItemMove } from '../../hooks/useCalendarItemMove';
import { useCalendarItemDragDrop } from '../../hooks/useCalendarItemDragDrop';
import { useCalendarDayFileDrop } from '../../hooks/useCalendarDayFileDrop';
import { isOsFileDrag } from '../../utils/calendarFileDrop';
import {
  gregorianDateFromJalaliKey,
  gregorianDateParam,
} from './calendarDayAddUtils';
import { jalaliMonths } from '../../utils/jalali';

interface Props {
  item: CalendarGridItem | null;
  dateKey: string;
  userId?: number | null;
  onWorkoutPress?: (workout: CalendarWorkout) => void;
  onReload?: () => void | Promise<void>;
  onUploadComplete?: (result: CalendarFileDropResult) => void | Promise<void>;
  onMetricMoved?: (targetDateIso: string) => void | Promise<void>;
}

/** Selected day panel — workouts below week strip (VO2 DS wo-card). */
export function CalendarDayDetail({ item, dateKey, userId, onWorkoutPress, onReload, onUploadComplete, onMetricMoved }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [actionTarget, setActionTarget] = useState<CalendarActionTarget | null>(null);
  const [copyWorkouts, setCopyWorkouts] = useState<CalendarWorkout[] | null>(null);
  const { athletes, isCoach } = useAthleteContext();
  const { user } = useAuth();

  const closeSheet = useCallback(() => setActionTarget(null), []);

  const handleDelete = useCallback(
    async (target: CalendarActionTarget) => {
      closeSheet();

      if (target.kind === 'metric') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteMetricTitle', 'Delete metric'),
          message: t('calendar.confirm.deleteMetricMessage', 'Delete this metric entry?'),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteMetric(target.uniqueCode);
          showToast(t('calendar.toast.metricDeleted', 'Metric deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.metricDeleteFailed', 'Failed to delete metric'));
        }
        return;
      }

      if (target.kind === 'health_document') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteHealthDocumentTitle', 'Delete health report'),
          message: t(
            'calendar.confirm.deleteHealthDocumentMessage',
            'Delete this report, its file, and all metrics saved from it?',
          ),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await healthDocuments.destroy(target.id);
          showToast(t('calendar.toast.healthDocumentDeleted', 'Health report deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.healthDocumentDeleteFailed', 'Failed to delete health report'));
        }
        return;
      }

      if (target.kind === 'event') {
        const ok = await confirm({
          title: t('calendar.confirm.deleteEventTitle', 'Delete event'),
          message: t('calendar.confirm.deleteEventMessage', { name: target.label }),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteEvent(target.id);
          showToast(t('calendar.toast.eventDeleted', { name: target.label }));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.eventDeleteFailed', 'Failed to delete event'));
        }
        return;
      }

      if (target.kind === 'note') {
        const ok = await confirm({
          title: t('calendar.actionSheet.deleteNote', 'Delete note'),
          message: t('calendar.confirm.deleteNoteMessage', 'Delete this note?'),
          variant: 'danger',
        });
        if (!ok) return;
        try {
          await forms.deleteNote(target.id);
          showToast(t('calendar.toast.noteDeleted', 'Note deleted'));
          await onReload?.();
        } catch {
          showToast(t('calendar.toast.noteDeleteFailed', 'Failed to delete note'));
        }
        return;
      }

      const ok = await confirm({
        title: t('calendar.confirm.deleteWorkoutTitle', 'Delete workout'),
        message: t('calendar.confirm.deleteWorkout', { title: target.title, defaultValue: `Delete "${target.title}"?` }),
        variant: 'danger',
      });
      if (!ok || !userId) return;

      try {
        await calendarActions.deleteWorkout(userId, target.id);
        showToast(t('calendar.toast.workoutDeleted', { title: target.title, defaultValue: `"${target.title}" deleted` }));
        await onReload?.();
      } catch {
        showToast(t('calendar.toast.workoutDeleteFailed', 'Failed to delete workout'));
      }
    },
    [closeSheet, confirm, onReload, showToast, t, userId],
  );

  const handleEdit = useCallback(
    (target: CalendarActionTarget) => {
      if (target.kind === 'metric') {
        navigate(`/calendar/metric/new?date=${target.dateParam}&code=${target.uniqueCode}`);
        return;
      }
      if (target.kind === 'workout') {
        onWorkoutPress?.({ id: target.id, title: target.title } as CalendarWorkout);
      }
    },
    [navigate, onWorkoutPress],
  );

  const testDateHint =
    gregorianDateFromJalaliKey(dateKey) ?? (item ? gregorianDateParam(item) : null);
  const goAdd = () => {
    if (testDateHint) navigate(`/calendar/workout/new?date=${testDateHint}`);
  };

  const addWorkoutCta = testDateHint ? (
    <button
      type="button"
      className="cal-day-detail__add-workout"
      onClick={goAdd}
      aria-label={t('calendar.day.addWorkout')}
    >
      <span className="cal-day-detail__add-workout-plus" aria-hidden>+</span>
      <span>{t('calendar.day.addWorkout')}</span>
    </button>
  ) : null;

  const fileDrop = useCalendarDayFileDrop({
    userId,
    testDateHint,
    onComplete: onUploadComplete,
  });

  const { requestMove, movePicker, moveItemToDate } = useCalendarItemMove({
    userId,
    onReload,
    onMoved: onMetricMoved,
    showToast,
  });

  const itemDrag = useCalendarItemDragDrop({ userId, moveItemToDate });
  const dateParamForDrop = item ? gregorianDateParam(item) : '';
  const metricDropProps = dateParamForDrop ? itemDrag.getDayDropProps(dateParamForDrop) : null;
  const metricDropClass =
    dateParamForDrop && itemDrag.isDayDropTarget(dateParamForDrop) ? ' cal-day-detail--metric-drag' : '';

  const dropZoneProps = {
    className: `cal-day-detail${item?.extra?.today ? ' cal-day-detail--with-readiness' : ''}${fileDrop.isFileDragOver ? ' cal-day-detail--file-drag' : ''}${metricDropClass}`,
    'data-calendar-date': dateParamForDrop || undefined,
    onDragOver: (e: React.DragEvent) => {
      fileDrop.onDragOver(e);
      metricDropProps?.onDragOver(e);
    },
    onDragLeave: (e: React.DragEvent) => {
      fileDrop.onDragLeave(e);
      metricDropProps?.onDragLeave(e);
    },
    onDrop: (e: React.DragEvent) => {
      if (isOsFileDrag(e)) {
        fileDrop.onDrop(e);
        return;
      }
      void metricDropProps?.onDrop(e);
    },
  };

  const dropHint = fileDrop.isFileDragOver ? (
    <div className="cal-day-detail__drop-hint" aria-hidden>
      {t('calendar.dropHint.file')}
    </div>
  ) : dateParamForDrop && itemDrag.isDayDropTarget(dateParamForDrop) ? (
    <div className="cal-day-detail__drop-hint" aria-hidden>
      {t('calendar.dropHint.item', 'Drop here')}
    </div>
  ) : null;

  if (!item?.extra) {
    return (
      <>
        <div {...dropZoneProps}>
          {dropHint}
          <p className="cal-day-detail__empty">{t('calendar.day.empty', 'No items this day')}</p>
          <div className="cal-day-detail__standalone-add">{addWorkoutCta}</div>
        </div>
        {fileDrop.uploadProgress && <CalendarUploadOverlay progress={fileDrop.uploadProgress} />}
      </>
    );
  }

  const extra = item.extra;
  const workouts = extra.workouts ?? [];
  const dateParam = gregorianDateParam(item);

  return (
    <>
      <div {...dropZoneProps}>
        {dropHint}

        {(extra.notes ?? []).map((note) => (
          <CalendarNoteRow
            key={note.id}
            content={note.content} title={note.title} description={note.description}
            dragProps={itemDrag.getNoteDragProps(note.id, dateParam, note.title ?? note.content ?? t('calendar.noteCard.label'))}
            onPress={() => navigate(`/calendar/note/new?date=${dateParam}&id=${note.id}`)}
            onLongPress={() => {
              setActionTarget({
                kind: 'note',
                id: note.id,
                label: note.title ?? note.content ?? t('calendar.noteCard.label'),
                dateParam,
              });
            }}
          />
        ))}

        {(extra.events ?? []).map((ev) => {
          const future = new Date() < new Date(ev.event_date);
          const untilEvent = future
            ? `${Math.floor((new Date(ev.event_date).getTime() - Date.now()) / 86400000)} روز دیگر`
            : 'تمام شد';
          return (
            <CalendarEventRow
              key={ev.id}
              event={ev}
              future={future}
              untilEvent={untilEvent}
              monthLabel={jalaliMonths[item.month]}
              dayLabel={item.day}
              dragProps={itemDrag.getEventDragProps(ev.id, dateParam, ev.name)}
              onPress={() =>
                navigate(`/calendar/event/new?date=${dateParam}&id=${ev.id}`, { state: { event: ev } })
              }
              onLongPress={() => {
                setActionTarget({
                  kind: 'event',
                  id: ev.id,
                  label: ev.name,
                  dateParam,
                });
              }}
            />
          );
        })}

        {(extra.healthDocuments ?? []).map((doc) => (
          <CalendarHealthDocumentRow
            key={doc.id}
            doc={doc}
            dragProps={itemDrag.getHealthDocumentDragProps(
              doc.id,
              dateParam,
              doc.original_filename,
              doc.unique_code,
            )}
            onDeleted={() => void onReload?.()}
            onLongPress={() => {
              setActionTarget({
                kind: 'health_document',
                id: doc.id,
                label: doc.original_filename,
                uniqueCode: doc.unique_code,
                dateParam,
              });
            }}
          />
        ))}

        {(extra.userMetrics ?? []).map((m, idx) => {
          const metric = m as {
            unique_code?: string;
            created_at?: string;
            metric_type_name?: string;
            value?: string | number;
            item_count?: number;
          };
          const label = `${metric.metric_type_name ?? 'Metric'}: ${metric.value ?? ''}`;
          return (
            <CalendarMetricRow
              key={metric.unique_code ?? idx}
              metric={metric}
              dragProps={
                metric.unique_code
                  ? itemDrag.getMetricDragProps(metric.unique_code, dateParam, label)
                  : undefined
              }
              onPress={() => {
                if (!metric.unique_code) {
                  navigate(`/calendar/metric/new?date=${dateParam}`);
                  return;
                }
                navigate(`/calendar/metric/new?date=${dateParam}&code=${encodeURIComponent(metric.unique_code)}`);
              }}
              onLongPress={() => {
                if (!metric.unique_code) return;
                setActionTarget({
                  kind: 'metric',
                  uniqueCode: metric.unique_code,
                  label,
                  dateParam,
                });
              }}
            />
          );
        })}

        <div className="wo-card-stack">
          {workouts.map((w) => (
            <CalendarWorkoutCard
              key={w.id}
              workout={w}
              onPress={() => onWorkoutPress?.(w)}
              onLongPress={() => {
                setActionTarget({
                  kind: 'workout',
                  id: w.id,
                  title: w.title ?? w.workout_type?.name ?? t('calendar.workout', 'Workout'),
                  dateParam,
                });
              }}
            />
          ))}
        </div>

        <div className="cal-day-detail__standalone-add">{addWorkoutCta}</div>

      </div>

      <CalendarItemActionSheet
        target={actionTarget}
        onClose={closeSheet}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onMove={requestMove}
        onCopyToAthletes={isCoach && athletes.length ? target => { if (target.kind === 'workout') setCopyWorkouts(workouts.filter(w => w.id === target.id)); } : undefined}
      />
      {copyWorkouts && <CalendarTransferModal workouts={copyWorkouts} athletes={athletes} ownerId={user?.id} sourceLabel={athletes.find(a => a.id === userId)?.full_name ?? user?.full_name ?? ''} onClose={() => setCopyWorkouts(null)} onCopied={() => { void onReload?.(); }} />}

      {movePicker}

      {fileDrop.uploadProgress && <CalendarUploadOverlay progress={fileDrop.uploadProgress} />}
    </>
  );
}
